/**
 * Header - Main navigation header with logo, search, cart, and user menu
 */
import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSearch, 
  FiShoppingCart, 
  FiHeart, 
  FiUser, 
  FiMenu, 
  FiX,
  FiChevronDown,
  FiPackage,
  FiLogOut,
  FiSettings,
  FiGrid
} from 'react-icons/fi';
import clsx from 'clsx';

import styles from './Header.module.css';
import { useCart, useWishlist, useAuth, useAppSelector, useAppDispatch, useBusinessRules } from '@/hooks';
import { ROUTES } from '@/config/routes';
import { APP_NAME } from '@/config/constants';
import { selectHeaderSettings } from '@/store/slices/settingsSlice';
import { openLoginPrompt } from '@/store/slices/uiSlice';
import { getActiveCategories } from '@/services/firebase/categories.service';
import { CategoryData } from '@/data/categories';
import { formatCurrency } from '@/utils';
import { CategoryIcon } from '@/components/common/CategoryIcon';

const Header: React.FC = memo(() => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [showAllDesktopCategories, setShowAllDesktopCategories] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  
  const userMenuRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const cart = useCart();
  const wishlist = useWishlist();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { freeShippingThreshold } = useBusinessRules();
  
  // Fetch categories from Firebase
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await getActiveCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);
  
  const cartCount = cart.itemCount;
  const cartTotal = cart.summary.subtotal;
  const wishlistCount = wishlist.count;

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`${ROUTES.PRODUCTS}?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  }, [searchQuery, navigate]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const handleLogout = useCallback(async () => {
    setIsUserMenuOpen(false);
    await logout();
  }, [logout]);

  const handleUserMenuItemClick = useCallback(() => {
    setIsUserMenuOpen(false);
  }, []);

  const headerSettings = useAppSelector(selectHeaderSettings);

  // Get display name for user
  const displayName = user?.displayName || `User ${user?.phoneNumber?.slice(-4) || ''}`;

  return (
    <header className={styles.header}>
      {/* Top Bar */}
      {(headerSettings?.showTopBar !== false) && <div className={styles.topBar}>
        <div className={styles.topBarContent}>
          <span>{headerSettings?.topBarText || `Free shipping on orders over ${formatCurrency(freeShippingThreshold)}`}</span>
          <div className={styles.topBarLinks}>
            <Link to={ROUTES.ABOUT}>About Us</Link>
            <Link to={ROUTES.CONTACT}>Contact</Link>
            <Link to={user ? ROUTES.ORDERS : ROUTES.TRACK_ORDER}>Track Order</Link>
          </div>
        </div>
      </div>}

      {/* Main Header */}
      <div className={styles.mainHeader}>
        <div className={styles.mainHeaderContent}>
          {/* Mobile Menu Toggle */}
          <button 
            className={styles.mobileMenuBtn}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>

          {/* Logo */}
          <Link to={ROUTES.HOME} className={styles.logo}>
            <span className={styles.logoIcon}>{(headerSettings?.logoText?.brand || APP_NAME.toUpperCase()).charAt(0)}</span>
            <span className={styles.logoText}>
              <span className={styles.logoBrand}>{headerSettings?.logoText?.brand || APP_NAME.toUpperCase()}</span>
              <span className={styles.logoTagline}>{headerSettings?.logoText?.tagline || 'DENTAL'}</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav}>
            <Link to={ROUTES.HOME} className={styles.navLink}>Home</Link>
            <div 
              className={styles.navDropdown}
              onMouseEnter={() => setIsCategoriesOpen(true)}
              onMouseLeave={() => { setIsCategoriesOpen(false); setShowAllDesktopCategories(false); }}
            >
              <button className={styles.navLink}>
                Categories
                <FiChevronDown className={clsx(styles.chevron, isCategoriesOpen && styles.open)} />
              </button>
              <AnimatePresence>
                {isCategoriesOpen && (
                  <motion.div 
                    className={styles.dropdownMenu}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className={styles.dropdownGrid}>
                      {(showAllDesktopCategories ? categories : categories.slice(0, 8)).map(category => (
                        <motion.div key={category.id} whileHover={{ x: 4 }} transition={{ duration: 0.15 }}>
                          <Link 
                            to={`${ROUTES.PRODUCTS}?category=${category.slug}`}
                            className={styles.dropdownItem}
                          >
                            <CategoryIcon image={category.image} icon={category.icon} name={category.name} size="sm" />
                            <span>{category.name}</span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                    {categories.length > 8 && (
                      <button
                        className={styles.dropdownViewAll}
                        onClick={() => setShowAllDesktopCategories(!showAllDesktopCategories)}
                      >
                        {showAllDesktopCategories ? 'Show Less' : `View All Categories (${categories.length})`}
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to={ROUTES.PRODUCTS} className={styles.navLink}>Products</Link>
            <Link to={ROUTES.BRANDS} className={styles.navLink}>Brands</Link>
            <Link to={ROUTES.CONTACT} className={styles.navLink}>Contact</Link>
          </nav>

          {/* Search Bar (Desktop) */}
          <form className={styles.searchForm} onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <button type="submit" className={styles.searchBtn} aria-label="Search">
              <FiSearch />
            </button>
          </form>

          {/* Action Icons */}
          <div className={styles.actions}>
            {/* Search Toggle (Mobile) */}
            <button 
              className={styles.actionBtn}
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              aria-label="Search"
            >
              <FiSearch />
            </button>

            {/* Wishlist */}
            {isAuthenticated ? (
              <Link to={ROUTES.WISHLIST} className={styles.actionBtn}>
                <FiHeart />
                {wishlistCount > 0 && (
                  <span className={styles.badge}>{wishlistCount}</span>
                )}
              </Link>
            ) : (
              <button
                className={styles.actionBtn}
                onClick={() => dispatch(openLoginPrompt())}
                aria-label="Wishlist"
              >
                <FiHeart />
              </button>
            )}

            {/* Cart */}
            <button
              className={cartCount > 0 ? styles.cartBtn : styles.actionBtn}
              onClick={() => cart.openDrawer()}
              aria-label="Open cart"
            >
              <FiShoppingCart />
              {cartCount > 0 && (
                <>
                  <span className={styles.badge}>{cartCount}</span>
                  <span className={styles.cartTotal}>{formatCurrency(cartTotal)}</span>
                </>
              )}
            </button>

            {/* User Menu / Login */}
            {isAuthenticated ? (
              <div className={styles.userMenuContainer} ref={userMenuRef}>
                <button 
                  className={clsx(styles.userBtn, isUserMenuOpen && styles.active)}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                >
                  <FiUser />
                  <FiChevronDown className={clsx(styles.userChevron, isUserMenuOpen && styles.open)} />
                </button>
                
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div 
                      className={styles.userMenu}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className={styles.userMenuHeader}>
                        <div className={styles.userAvatar}>
                          <FiUser />
                        </div>
                        <div className={styles.userInfo}>
                          <span className={styles.userName}>{displayName}</span>
                          <span className={styles.userPhone}>+91 {user?.phoneNumber}</span>
                        </div>
                      </div>
                      
                      <div className={styles.userMenuDivider} />
                      
                      <Link 
                        to={ROUTES.PROFILE} 
                        className={styles.userMenuItem}
                        onClick={handleUserMenuItemClick}
                      >
                        <FiSettings />
                        <span>My Profile</span>
                      </Link>
                      <Link 
                        to={ROUTES.ORDERS} 
                        className={styles.userMenuItem}
                        onClick={handleUserMenuItemClick}
                      >
                        <FiPackage />
                        <span>My Orders</span>
                      </Link>
                      <Link 
                        to={ROUTES.WISHLIST} 
                        className={styles.userMenuItem}
                        onClick={handleUserMenuItemClick}
                      >
                        <FiHeart />
                        <span>My Wishlist</span>
                      </Link>
                      
                      {isAdmin && (
                        <>
                          <div className={styles.userMenuDivider} />
                          <Link 
                            to={ROUTES.ADMIN_DASHBOARD} 
                            className={styles.userMenuItemAdmin}
                            onClick={handleUserMenuItemClick}
                          >
                            <FiGrid />
                            <span>Admin Panel</span>
                          </Link>
                        </>
                      )}
                      
                      <div className={styles.userMenuDivider} />
                      
                      <button 
                        className={styles.userMenuLogout}
                        onClick={handleLogout}
                      >
                        <FiLogOut />
                        <span>Logout</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to={ROUTES.LOGIN} className={styles.actionBtn}>
                <FiUser />
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Search */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div 
              className={styles.mobileSearch}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <form onSubmit={handleSearch} className={styles.mobileSearchContainer}>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                  autoFocus
                />
                <button type="submit" className={styles.searchBtn} aria-label="Search">
                  <FiSearch />
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav 
            className={styles.mobileNav}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <div className={styles.mobileNavContent}>
              {/* User Section in Mobile Nav */}
              {isAuthenticated && (
                <Link to={ROUTES.PROFILE} className={styles.mobileUserSection} onClick={toggleMobileMenu}>
                  <div className={styles.mobileUserAvatar}>
                    <FiUser />
                  </div>
                  <div className={styles.mobileUserInfo}>
                    <span className={styles.mobileUserName}>{displayName}</span>
                    <span className={styles.mobileUserPhone}>+91 {user?.phoneNumber}</span>
                  </div>
                </Link>
              )}
              
              <Link to={ROUTES.HOME} className={styles.mobileNavLink} onClick={toggleMobileMenu}>
                Home
              </Link>
              <Link to={ROUTES.PRODUCTS} className={styles.mobileNavLink} onClick={toggleMobileMenu}>
                All Products
              </Link>
              <div className={styles.mobileNavSection}>
                <span className={styles.mobileNavTitle}>Categories</span>
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03, duration: 0.2 }}
                  >
                    <Link 
                      to={`${ROUTES.PRODUCTS}?category=${category.slug}`}
                      className={styles.mobileNavSubLink}
                      onClick={toggleMobileMenu}
                    >
                      <CategoryIcon image={category.image} icon={category.icon} name={category.name} size="sm" />
                      <span>{category.name}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
              <Link to={ROUTES.BRANDS} className={styles.mobileNavLink} onClick={toggleMobileMenu}>
                Brands
              </Link>
              <Link to={ROUTES.CONTACT} className={styles.mobileNavLink} onClick={toggleMobileMenu}>
                Contact Us
              </Link>
              <Link to={ROUTES.ABOUT} className={styles.mobileNavLink} onClick={toggleMobileMenu}>
                About Us
              </Link>
              
              {/* Account Section */}
              <div className={styles.mobileNavSection}>
                <span className={styles.mobileNavTitle}>Account</span>
                {isAuthenticated ? (
                  <>
                    <Link 
                      to={ROUTES.PROFILE} 
                      className={styles.mobileNavSubLink}
                      onClick={toggleMobileMenu}
                    >
                      <FiSettings />
                      <span>My Profile</span>
                    </Link>
                    <Link 
                      to={ROUTES.ORDERS} 
                      className={styles.mobileNavSubLink}
                      onClick={toggleMobileMenu}
                    >
                      <FiPackage />
                      <span>My Orders</span>
                    </Link>
                    <Link 
                      to={ROUTES.WISHLIST} 
                      className={styles.mobileNavSubLink}
                      onClick={toggleMobileMenu}
                    >
                      <FiHeart />
                      <span>My Wishlist ({wishlistCount})</span>
                    </Link>
                    {isAdmin && (
                      <Link 
                        to={ROUTES.ADMIN_DASHBOARD} 
                        className={styles.mobileNavSubLinkAdmin}
                        onClick={toggleMobileMenu}
                      >
                        <FiGrid />
                        <span>Admin Panel</span>
                      </Link>
                    )}
                    <button 
                      className={styles.mobileNavLogout}
                      onClick={() => {
                        toggleMobileMenu();
                        handleLogout();
                      }}
                    >
                      <FiLogOut />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link 
                    to={ROUTES.LOGIN} 
                    className={styles.mobileNavSubLink}
                    onClick={toggleMobileMenu}
                  >
                    <FiUser />
                    <span>Login / Sign Up</span>
                  </Link>
                )}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleMobileMenu}
          />
        )}
      </AnimatePresence>

    </header>
  );
});

Header.displayName = 'Header';

export default Header;
